import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  lowerCase,
  set,
} from 'lodash';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import BenefitsModal from '../BenefitsModal';
import displayCurrency from '../../../../helpers/displayCurrency';
import UpsellButton from '../../../../common/components/UpsellButton/UpsellButton';
import Loader from '../../../../common/components/Loader/Loader';
import { hasPermission } from '../../../auth/redux/authActions';
import './styles.scss';
import { Icon } from '../../../../common/components/index';
import ReactMarkdown from 'react-markdown';
import styled from 'styled-components';
import img from './images/info.png';

/**
 * Used to avoid non-superuser to select the quotes that have the errors object filled.
 */
const isPermitted = (cell) => {
  if (hasPermission('policy.underwriter')) {
    return true;
  } else if (cell?.errors?.length) {
    return false;
  }
  return true;
};

const setTableOption = ( isSaving, isCalculating) => {

  if (isSaving) {
    return  'Saving Quote';
  }
  if (isCalculating) {
    return ( <Loader/>);
  }
  return 'No Quotes Found';

};

const hasAnnualScheme = (scheme, quotes, isMta) => {
  if (isMta) {
    return undefined;
  }

  return quotes?.find((quote) => {
    const premiumScheme = quote?.meta?.scheme;
    return (
      premiumScheme?.scheme_type === 'annual' &&
      scheme?.insurer === premiumScheme?.insurer &&
      scheme?.cover_level === premiumScheme?.cover_level
    );
  });
};

const formatQuotes = (
  isCalculating,
  availableQuotes,
  isAnnual,
  isMta,
  selectedSchemes,
  premiumIndication,
  isWhiteLabel
) => {
  if (isCalculating) {
    return [];
  }
  const singleAnnualDiff = (single, annual) => Math.abs(single - annual);
  // Filtering quotes according to the scheme_type
  const schemeTypeQuotes = isAnnual
    ? availableQuotes.filter((quote) => {
      return quote?.meta?.scheme?.scheme_type === 'annual';
    })
    : availableQuotes.filter((quote) => {
      return quote?.meta?.scheme?.scheme_type === 'single';
    });
  return schemeTypeQuotes.map( (quote) => {
    const benefits = quote?.meta?.benefits ?? [];
    const annualQuote = !isAnnual
      ? hasAnnualScheme(quote.meta.scheme, availableQuotes, isMta)
      : false;
    if (annualQuote && annualQuote?.meta?.premium?.gross !== 0) {
      const upgradediff =
        singleAnnualDiff(
        quote?.meta?.premium?.gross,
        annualQuote?.meta?.premium?.gross,
      );
      set(
        annualQuote,
        'meta.annual_upgrade_diff',
        displayCurrency(upgradediff, quote?.meta?.premium?.currency),
      );
    }

    if (
      selectedSchemes?.length === 1 &&
      !isMta &&
      premiumIndication?.meta?.scheme?.id ===
      quote?.meta.scheme.id
    ) {
      //this is the selected scheme so show its actual value in table
      quote = premiumIndication;
    }

    // Used to return N/A for quotes with errors when the user does not have permission
    const notPermittedQuote = {
      meta: {
        premium_before_adjustments: {
          gross: 0,
        },
        scheme: {
          id: '000000000000000001',
        },
        premium: {
          gross: 0,
        },
      },
    };

    const medicalText = benefits.find( (benefit) => {
      return benefit?.display_name === 'Medical';
    });

    const cancellationText = benefits.find( (benefit) => {
      return benefit?.display_name === 'Cancellation';
    });
    const baggageText =
      benefits.find((benefit) => {
        return benefit?.display_name === 'Baggage';
      });
    const personalText = benefits.find( (benefit) => {
      return benefit?.display_name === 'Personal Accident';

    });
    return {
      underwriter: {
        logo: quote?.meta.scheme.logo,
        is_whitelabel: isWhiteLabel,
        insurer: quote?.meta?.scheme?.insurer ?? '',
      },
      scheme: quote?.meta?.scheme?.cover_level,
      medical: medicalText?.text,
      cancellation: cancellationText?.text,
      baggage_limit: baggageText?.text,
      personal: personalText?.text,
      benefits: quote?.meta,
      single: isPermitted(quote?.meta)
        ? quote?.meta
        : notPermittedQuote.meta,
      annual: isPermitted(quote?.meta)
        ? isAnnual
          ? quote?.meta
          : annualQuote?.meta
        : notPermittedQuote.meta,
      errors: quote?.meta.errors,
      declines: quote?.meta.declines,
      status: quote?.meta.scheme.status,
    };
  });

};


const schemeIsSelected = (schemeId, selectedSchemes) => {

  const selected = selectedSchemes?.find((scheme) => {
    return scheme.id === schemeId;
  });
  return !!selected;
};

const ExpandComponent = ({
  errors,
  referrals,
  declines,
  information,
  coverLevel
}) => {
  const showErrorBox =
    !!errors?.length || !!referrals?.length || !!declines?.length;
  const customMessages = {
    'Additional Option: Cruise Cover is not available':
      'This policy does not include cruise benefits',
    "Medical Declaration Question 'is-terminal' is not covered":
      'This insurer does not provide cover for terminal illness',
  };

  const declineReasons =
    declines?.reduce((carry, decline) => {
        const reason = decline?.reason;
        if (carry.indexOf(reason) < 0) {
          carry.push(reason);
        }
        return carry;
      },
      [],
    );

  if (showErrorBox) {
    return (
      <div className="extra-row">
        {!!errors?.length && (
          <div className="quote-error-box">
                <span>
                  NOTE: &nbsp;
                  {errors.map((error, i) => {
                    const message = customMessages?.[error] ?? error;
                    return !i ? message : ', ' + message;
                  })}
                </span>
          </div>
        )}
        {!!declineReasons?.length && (
          <div className="quote-error-box">
            <span>
              {declineReasons.map((decline, i) => {
                return <li key={i}>{decline}</li>;
              })}
            </span>
          </div>
        )}
        {!!referrals?.length && (
          <div className="quote-error-box">
            <ul>
              {referrals.map((referral, i) => {
                return <li key={i}>{referral}</li>;
              })}
            </ul>
          </div>
        )}
      </div>
    );
  }

  const advisoryMessage = information?.available_options?.advisory_message;
  if (advisoryMessage?.auto_include) {
    const img = require('./images/info.png');
    const Link = styled.a.attrs({
      target: '_blank',
    })`
      color: blue;
    `;
    const renderer = {
      link: props => <Link {...props} />,
    };


    const message = advisoryMessage?.display_name;
    const displayMessage = typeof message === 'object'  ? message?.[coverLevel] : message;
    if (!displayMessage) {
      return null;
    }

      return (
        <div className="extra-row">
          <div className="advisory-message-box">
            <span>
              <ReactMarkdown
                source={`![](${img}) ${displayMessage}`}
                renderers={renderer}
              />
            </span>
          </div>
        </div>
      );
    }
  return null;
};


const SingleContainer = ({ children, taxRate, gross }) => (
  <div className="quote-value-container single-premium">
    <div className="quote-value-premium">
      <div className="dummy-upsell" style={{ height: '19px' }}/>
      {children}
      <span className="incl-ipt-mssg">
        {gross ? `inc. ${taxRate ?? ''}% IPT` : ''}
      </span>
    </div>
  </div>
);

const AnnualContainer = ({ children, upgrade, gross, taxRate }) => (
  <div className="quote-value-container annual-premium">
    <div className="quote-value-premium">
      {upgrade ? (
        <UpsellButton
          color={'#ce263a'}
          content={`Upgrade to annual for only ${upgrade}  extra`}
        />
      ) : (
        <UpsellButton color="transparent" content={''}/>
      )}
      {children}
    </div>
    <span className="incl-ipt-mssg">
      {gross ? `inc. ${taxRate ?? ''}% IPT` : ''}
    </span>
  </div>

);
const Premium = ({
  cell,
  isSchemeSelected,
  isMta,
  onSchemeSelect,
}) => {
  if (!cell) {
    return (
      <span className="premium">
          <div className="quote-value">
            <span>N/A</span>
            <span className="icon-padding">
              <Icon name="circle" style="far"/>
            </span>
          </div>
        </span>
    );
  }

  const grossPremiumBeforeAdjustment = cell?.premium_before_adjustments?.gross ?? false;

  const grossPremium = cell.premium?.gross ?? false;
  const currency = cell.premium?.currency;
  const hasGross = cell.premium && (cell.premium?.gross > 0 || isMta);

  const hasGrossAndIsPermitted = cell && hasGross && isPermitted(cell);
  const zeroPremiumDisplay = isMta && cell ? '0.00' : 'N/A';
  const premiumClassName = isSchemeSelected ? 'premium selected' : 'premium';
  const iconClass = isSchemeSelected ? 'check-circle' : 'circle';
  const handleSelect = () => {
    hasGrossAndIsPermitted ? onSchemeSelect(cell?.scheme?.id) : false;
  };


  return (
    <span
      className={premiumClassName}
      onClick={handleSelect}
    >
        <div className={isSchemeSelected ? 'quote-value-selected' : 'quote-value'}>
          {grossPremiumBeforeAdjustment !== grossPremium && (
            <span className="previousPremium">
              {' '}
              {displayCurrency(grossPremiumBeforeAdjustment, currency)}
            </span>
          )}
          <span>
            {' '}
            {hasGrossAndIsPermitted
              ? displayCurrency(grossPremium, currency)
              : zeroPremiumDisplay}
          </span>
          <span className="icon-padding">
            <Icon name={iconClass} style="far"/>
          </span>
        </div>
      </span>
  );
};

const AvailableQuotes = ({
  availableQuotes,
  schemeType,
  tableWidths,
  product,
  selectedSchemes,
  mta,
  handleSchemeSelect,

}) => {




  const renderBenefits = (cell) => {
    if (!cell) {
      return null;
    }
    return (
      <div className="quote-value-container">
        <BenefitsModal
          scheme={cell.scheme}
          benefits={cell.benefits}
          excessWaiver={cell.information?.available_options?.excess_waiver?.selected ?? false}
        />
      </div>
    );
  };


  const renderSingle = (cell) => {

    const isSelected = schemeIsSelected(cell?.scheme?.id, selectedSchemes);

    return (
      <SingleContainer
        taxRate={cell?.rates?.tax_rate}
        gross={cell?.premium?.gross}
      >
        <Premium
          isSchemeSelected={isSelected}
          onSchemeSelect={handleSchemeSelect}
          cell={cell}
          isMta={mta}
        />
      </SingleContainer>

    );
  };

  const renderAnnual = (cell) => {
    const upgrade = cell?.annual_upgrade_diff ?? false;
    const gross = cell?.premium?.gross ?? false;
    const isSelected = schemeIsSelected(cell?.scheme?.id, selectedSchemes);
    return (
      <AnnualContainer
        upgrade={upgrade}
        taxRate={cell?.rates?.tax_rate}
        gross={gross}
      >
        <Premium
          isSchemeSelected={isSelected}
          onSchemeSelect={handleSchemeSelect}
          cell={cell}
          isMta={mta}
        />
      </AnnualContainer>
    );
  };

  const expandComponent = (row) => {
    const tripType = row.benefits?.scheme?.scheme_type;
    return (
      <ExpandComponent
        errors={row.errors}
        referrals={row.referrals}
        declines={row.declines}
        benefits={row.benefits}
        coverLevel={row.scheme}
        information={row?.[tripType]?.information}
      />
    );
  };



  const isAnnual = schemeType !== 'single';

  const isWhiteLabel =
    product?.source?.attributes?.is_broker &&
    product?.source?.attributes?.is_channel;
  const quotes  = formatQuotes(product.isCalculating,availableQuotes, isAnnual, mta, selectedSchemes, product,isWhiteLabel);

  const expandBodyClass = 'available-quotes-extra-row advisory-row';
  let tableOptions = {
    noDataText: setTableOption( product.isSaving, product.isCalculating),
    expandBodyClass: expandBodyClass,
    defaultSortName: !isAnnual ? 'single' : 'annual',
    defaultSortOrder: 'desc',
  };
  return (
    <div>
      <div className="col-xs-12 available-quotes">
        <BootstrapTable
          replace
          key={isAnnual}
          data={quotes}
          name="quotes"
          expandableRow={isExpandableRow}
          expandComponent={expandComponent}
          options={tableOptions}
          trClassName={rowClassName}
          height="600px"
        >
          <TableHeaderColumn
            isKey
            dataField="underwriter"
            dataFormat={renderProductLogo}
            width={tableWidths.logo}
            className="quotes-header"
            columnClassName="quotes-td"
          >
            Underwriter
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="scheme"
            className="quotes-header"
            dataFormat={renderCoverLevel}
            width={tableWidths.scheme}
            columnClassName="quotes-td"
          >
            Scheme
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="medical"
            className="quotes-header"
            width={tableWidths.benefit}
            dataFormat={renderBenefit}
            columnClassName="quotes-td"
          >
            Medical
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="cancellation"
            className="quotes-header"
            width={tableWidths.benefit}
            dataFormat={renderBenefit}
            columnClassName="quotes-td"
          >
            Cancellation
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="baggage_limit"
            className="quotes-header"
            width={tableWidths.benefit}
            dataFormat={renderBenefit}
            columnClassName="quotes-td"
          >
            Baggage Limit
          </TableHeaderColumn>
          {isAnnual && (
            <TableHeaderColumn
              dataField="annual"
              className="quotes-header"
              width={tableWidths.benefit}
              dataFormat={renderStandardExcess}
              columnClassName="quotes-td"
            >
              Standard Excess
            </TableHeaderColumn>
          )}
          {!isAnnual && (
            <TableHeaderColumn
              dataField="single"
              className="quotes-header"
              width={tableWidths.benefit}
              dataFormat={renderStandardExcess}
              columnClassName="quotes-td"
            >
              Standard Excess
            </TableHeaderColumn>
          )}
          <TableHeaderColumn
            dataField="benefits"
            dataFormat={renderBenefits}
            width={tableWidths.benefits}
            className="quotes-header"
            columnClassName="quotes-td"
          >
            Benefits
          </TableHeaderColumn>
          {!isAnnual && (
            <TableHeaderColumn
              dataField="single"
              dataSort
              sortFunc={sortQuote}
              sortFuncExtraData={'single'}
              dataFormat={renderSingle}
              className="quotes-header"
              columnClassName="quotes-td"
              width={tableWidths.quote}
            >
              Single Trip
            </TableHeaderColumn>
          )}
          <TableHeaderColumn
            dataField="annual"
            dataSort
            sortFunc={sortQuote}
            sortFuncExtraData={'annual'}
            dataFormat={renderAnnual}
            className="quotes-header"
            columnClassName="quotes-td"
            width={tableWidths.quote}
          >
            Annual
          </TableHeaderColumn>
        </BootstrapTable>
      </div>
    </div>
  );
};

const renderProductLogo = (cell) => {
  return (
    <div>
      <div className="quote-logo-container">
        <div className="quote-logo">
          {cell.is_whitelabel && cell.insurer && <span>{cell.insurer}</span>}
          <img className="img-responsive" src={cell.logo} alt={cell.insurer}/>
        </div>
      </div>
    </div>
  );
};

const renderCoverLevel = (cell) => {
  let cellStyle = lowerCase(cell) + '-scheme';
  return (
    <div>
      <div className={'quote-value-container ' + cellStyle}>
        <span>{cell}</span>
      </div>
    </div>
  );
};

const renderBenefit = (cell) => {
  if (!cell) {
    return (
      <div className="quote-value-container">
        <span className="benefit">Excluded</span>
      </div>
    );
  }
  return (
    <div className="quote-value-container">
      <span className="benefit">{cell}</span>
    </div>
  );
};

const renderStandardExcess = (cell) => {
  let standardExcess = cell?.information?.standard_excess ?? '';
  const excessWaiver = cell?.information?.available_options?.excess_waiver?.selected ?? false;

  if (excessWaiver) {
    standardExcess = 'Nil';
  }

  return (
    <div className="quote-value-container">
      <span className="excess">{standardExcess}</span>
    </div>
  );
};

const sortQuote = (a, b, order, type) => {
  const aquote = type === 'single' ? a?.single : a?.annual;
  const bquote = type === 'single' ? b?.single : b?.annual;
  const aprem = !aquote?.errors?.length ? aquote?.premium?.gross ?? 0 : 0;
  const bprem =   !bquote?.errors?.length ? bquote?.premium?.gross ?? 0 : 0;

  if (aprem === 0 && bprem === 0) {
    return 0;
  }
  if (aprem === 0) {
    return 1;
  }
  if (bprem === 0) {
    return -1;
  }

  if (order === 'asc') {
    return aprem > bprem ? -1 : aprem === bprem ? 0 : 1;
  } else {
    return bprem > aprem ? -1 : bprem === aprem ? 0 : 1;
  }
};

const isExpandableRow = (row) => {

  const tripType = row?.benefits?.scheme?.scheme_type;
  const availableOptions = row?.[tripType]?.information?.available_options;
  return !row?.errors?.length || !row?.declines?.length || availableOptions?.auto_include;
};

const rowClassName = (row) => {
  let defaultRowClassName = 'available-quotes-tr';
  const tripType = row?.benefits?.scheme?.scheme_type;
  const availableOptions = row?.[tripType]?.information?.available_options ?? {};
  if ((availableOptions && availableOptions.advisory_message && availableOptions.advisory_message.auto_include)) {
    defaultRowClassName = 'available-quotes-tr advisory-row-insurer';
  }
  return (row.status === 'test' ? 'testScheme ' : ' ') + defaultRowClassName;
};

AvailableQuotes.propTypes = {
  product: PropTypes.object.isRequired,
  currentProduct: PropTypes.object.isRequired,
  availableQuotes: PropTypes.array.isRequired,
  selectedSchemes: PropTypes.array.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isTestCenter: PropTypes.bool,
  schemeType: PropTypes.string,
};

AvailableQuotes.defaultProps = {
  tableWidths: {
    logo: '14%',
    scheme: '14%',
    benefit: '13%',
    benefits: '7%',
    quote: '13%',
  },
};

export default connect()(AvailableQuotes);
